import { nanoid } from 'nanoid';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { ListProps } from '../protocols';
import { Content, Option } from '../styles';

const ITEMS_PER_PAGE = 50;
const SCROLL_UNLOCK_DELAY = 80;

type ListaOption = NonNullable<ListProps['options']>[number];

const normalizeText = (value = ''): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();

/**
 * Lista de opções com filtro, navegação por teclado e carregamento progressivo.
 */
export const Lista = ({
  listaWidth,
  listaHeight,
  setIsOpen,
  filter,
  setFilter,
  options = [],
  changeSelected,
}: ListProps): JSX.Element => {
  const [renderItem, setRenderItem] = useState<number>(ITEMS_PER_PAGE);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [mouseFocusedIndex, setMouseFocusedIndex] = useState<number>(-1);

  const wrapperRef = useRef<HTMLUListElement | null>(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<number | null>(null);
  const duplicatedKeyMapRef = useRef<WeakMap<ListaOption, string>>(
    new WeakMap(),
  );

  const closeList = useCallback(() => {
    setIsOpen(false);
    setFilter('');
  }, [setIsOpen, setFilter]);

  const loadMore = useCallback(() => {
    setRenderItem((previousValue) => previousValue + ITEMS_PER_PAGE);
  }, []);

  const normalizedFilter = useMemo(() => normalizeText(filter), [filter]);

  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      normalizeText(String(option?.label ?? '')).includes(normalizedFilter),
    );
  }, [options, normalizedFilter]);

  const renderedOptions = useMemo(() => {
    const valueCount = filteredOptions.reduce<Record<string, number>>(
      (accumulator, option) => {
        const valueKey = String(option?.value);
        accumulator[valueKey] = (accumulator[valueKey] || 0) + 1;
        return accumulator;
      },
      {},
    );

    return filteredOptions.map((option) => {
      const valueKey = String(option?.value);

      if (valueCount[valueKey] === 1) {
        return {
          ...option,
          stableKey: valueKey,
        };
      }

      const existingKey = duplicatedKeyMapRef.current.get(option);

      if (existingKey) {
        return {
          ...option,
          stableKey: existingKey,
        };
      }

      const generatedKey = `${valueKey}-${nanoid()}`;
      duplicatedKeyMapRef.current.set(option, generatedKey);

      return {
        ...option,
        stableKey: generatedKey,
      };
    });
  }, [filteredOptions]);

  useEffect(() => {
    setRenderItem(ITEMS_PER_PAGE);
    setFocusedIndex(-1);
    setMouseFocusedIndex(-1);
  }, [normalizedFilter, options]);

  useEffect(() => {
    if (!renderedOptions.length) {
      setFocusedIndex(-1);
      setMouseFocusedIndex(-1);
      return;
    }

    setFocusedIndex((previousValue) =>
      previousValue >= renderedOptions.length
        ? renderedOptions.length - 1
        : previousValue,
    );

    setMouseFocusedIndex((previousValue) =>
      previousValue >= renderedOptions.length
        ? renderedOptions.length - 1
        : previousValue,
    );
  }, [renderedOptions.length]);

  useEffect(() => {
    const wrapperElement = wrapperRef.current;
    if (!wrapperElement) {
      return undefined;
    }

    const handleScroll = () => {
      isScrollingRef.current = true;

      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }

      const { scrollTop, scrollHeight, clientHeight } = wrapperElement;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 5;

      if (isNearBottom && renderItem < renderedOptions.length) {
        loadMore();
      }

      scrollTimeoutRef.current = window.setTimeout(() => {
        isScrollingRef.current = false;
      }, SCROLL_UNLOCK_DELAY);
    };

    wrapperElement.addEventListener('scroll', handleScroll);

    return () => {
      wrapperElement.removeEventListener('scroll', handleScroll);

      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [loadMore, renderItem, renderedOptions.length]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const wrapperElement = wrapperRef.current;
      if (!wrapperElement) {
        return;
      }

      const optionElements = Array.from(
        wrapperElement.getElementsByTagName('li'),
      ) as HTMLLIElement[];

      if (!optionElements.length) {
        return;
      }

      const firstOptionHeight = optionElements[0]?.clientHeight || 1;
      const pageSize = Math.max(
        1,
        Math.floor(wrapperElement.clientHeight / firstOptionHeight),
      );

      const getSafeIndex = (index: number): number =>
        Math.max(0, Math.min(index, optionElements.length - 1));

      const moveFocus = (nextIndex: number) => {
        if (isScrollingRef.current) {
          return;
        }

        setFocusedIndex(getSafeIndex(nextIndex));
      };

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveFocus(focusedIndex + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          moveFocus(focusedIndex - 1);
          break;
        case 'PageDown':
          event.preventDefault();
          moveFocus(focusedIndex + pageSize);
          break;
        case 'PageUp':
          event.preventDefault();
          moveFocus(focusedIndex - pageSize);
          break;
        case 'Home':
          event.preventDefault();
          moveFocus(0);
          break;
        case 'End':
          event.preventDefault();
          moveFocus(optionElements.length - 1);
          break;
        case 'Enter': {
          event.preventDefault();

          const activeIndex =
            focusedIndex >= 0 && focusedIndex < optionElements.length
              ? focusedIndex
              : mouseFocusedIndex;

          if (activeIndex >= 0 && activeIndex < optionElements.length) {
            optionElements[activeIndex].click();
          }
          break;
        }
        case 'Escape':
          event.preventDefault();
          closeList();
          break;
        default:
          break;
      }
    },
    [closeList, focusedIndex, mouseFocusedIndex],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    const wrapperElement = wrapperRef.current;
    if (!wrapperElement) {
      return;
    }

    const optionElements = wrapperElement.getElementsByTagName('li');

    if (focusedIndex < 0 || focusedIndex >= optionElements.length) {
      return;
    }

    const focusedOption = optionElements[focusedIndex] as HTMLElement;
    const { clientHeight: wrapperHeight } = wrapperElement;
    const { offsetTop, offsetHeight } = focusedOption;

    wrapperElement.scrollTop = offsetTop - wrapperHeight / 2 + offsetHeight / 2;
    focusedOption.focus();
  }, [focusedIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        closeList();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeList]);

  const visibleOptions = useMemo(
    () => renderedOptions.slice(0, renderItem),
    [renderItem, renderedOptions],
  );

  const content = useMemo(() => {
    if (!options.length || !renderedOptions.length) {
      return <Option className="not-found">Nenhum registro encontrado.</Option>;
    }

    return (
      <InfiniteScroll
        dataLength={visibleOptions.length}
        next={loadMore}
        hasMore={renderItem < renderedOptions.length}
        loader={<small>Carregando...</small>}
        height={listaHeight}
        scrollThreshold="90%"
      >
        {visibleOptions.map((option, index) => {
          const isFocused = focusedIndex === index;

          return (
            <Option
              key={option.stableKey}
              title={option.label}
              className={isFocused ? 'focused' : ''}
              tabIndex={0}
              role="option"
              aria-selected={isFocused}
              onMouseEnter={() => setMouseFocusedIndex(index)}
              onMouseLeave={() => setMouseFocusedIndex(-1)}
              onClick={() => {
                changeSelected(option);
                closeList();
              }}
            >
              {option.icon && (
                <span style={{ marginRight: '0.625rem' }}>{option.icon}</span>
              )}
              {option.label}
            </Option>
          );
        })}
      </InfiniteScroll>
    );
  }, [
    changeSelected,
    closeList,
    focusedIndex,
    listaHeight,
    loadMore,
    options.length,
    renderItem,
    renderedOptions.length,
    visibleOptions,
  ]);

  return (
    <Content listaWidth={listaWidth}>
      <ul
        ref={wrapperRef}
        role="listbox"
        style={options.length ? { height: listaHeight } : undefined}
      >
        {content}
      </ul>
    </Content>
  );
};
